"use client";

import * as React from "react";
import ReactECharts from "echarts-for-react";

import type { ChartVizSpec, QuerySpec } from "@/lib/reports/schema";
import type { QueryResult } from "@/lib/reports/adapters/types";
import { humanizeMeasure, humanizeDimension } from "@/lib/reports/labels";
import { buildOption, FLOATS_PALETTE } from "./charts";

export interface FloatsChartProps {
  viz: ChartVizSpec;
  query: QuerySpec;
  data: QueryResult;
  /** Theme palette override; defaults to the Floats accent palette. */
  palette?: string[];
  /**
   * Click on a data point — emits (member, value) for the coordinator.
   * TODO wire: ReportBlock renders FloatsChart without passing onSelect, so
   * cross-block drill-through isn't active yet. Wire it through ReportBlock →
   * DashboardRenderer → coordinator when drill-through ships.
   */
  onSelect?: (event: { member: string; value: string | number; raw: Record<string, unknown> }) => void;
  height?: number | string;
}

export function FloatsChart({ viz, query, data, palette, onSelect, height = "100%" }: FloatsChartProps) {
  const option = React.useMemo(() => buildOption(viz, query, data, palette ?? FLOATS_PALETTE), [viz, query, data, palette]);
  const chartRef = React.useRef<ReactECharts | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  // Accessibility: ECharts renders to <canvas>, which is opaque to
  // screen readers. Build a short text description from the spec so
  // assistive tech gets at minimum: chart type, measure, and row count.
  const ariaSummary = React.useMemo(() => {
    const chartTypeLabel = viz.type.replace(/_/g, " ");
    const measureLabel = query.measures.map((m) => humanizeMeasure(m)).join(", ");
    const dimLabel = (query.dimensions ?? []).map(humanizeDimension).join(", ");
    const rowCount = data.rows?.length ?? 0;
    const parts = [`${chartTypeLabel} chart`];
    if (measureLabel) parts.push(`of ${measureLabel}`);
    if (dimLabel) parts.push(`by ${dimLabel}`);
    parts.push(`(${rowCount} ${rowCount === 1 ? "row" : "rows"})`);
    return parts.join(" ");
  }, [viz.type, query.measures, query.dimensions, data.rows]);

  // Resize observer: ECharts initialises to the container's pixel size
  // at mount and doesn't re-measure on its own. When a block is dragged
  // wider/taller in the layout grid, or the browser viewport changes,
  // the chart stays at its original size and overflows / underflows.
  // Watch the container and call echarts' .resize() whenever it changes.
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      const instance = chartRef.current?.getEchartsInstance?.();
      // resize() is a no-op when the size hasn't actually changed, so
      // this is cheap to call on every observer tick.
      instance?.resize?.();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const onEvents = React.useMemo(() => {
    if (!onSelect) return undefined;
    return {
      click: (params: { name?: string; value?: unknown; data?: unknown; seriesName?: string }) => {
        const dimensionMember = query.dimensions?.[0] ?? query.timeDimension?.member;
        if (!dimensionMember) return;
        const raw = (params.data ?? {}) as Record<string, unknown>;
        const value = (raw[dimensionMember] ?? params.name) as string | number | undefined;
        if (value === undefined) return;
        onSelect({ member: dimensionMember, value, raw });
      },
    };
  }, [onSelect, query]);

  // ECharts initialises with the *current* pixel size of its container. When
  // the chart is dropped into a flex parent that hasn't laid out yet, that
  // size is 0×0 and no canvas appears. Wrap it in an absolutely-positioned
  // Per-chart-type minimum height so a 1-row block doesn't compress
  // the chart into an illegible band. Funnel/sankey/cohort/heatmap
  // need more vertical room; KPI-like charts (gauge) need less.
  const chartTypeMin: Record<string, number> = {
    line: 220,
    area: 220,
    bar: 220,
    horizontal_bar: 220,
    stacked_bar: 220,
    scatter: 240,
    heatmap: 260,
    funnel: 240,
    cohort: 280,
    sankey: 280,
    treemap: 240,
    waterfall: 220,
    gauge: 200,
    radar: 280,
    calendar_heatmap: 160,
    boxplot: 240,
    pie: 240,
    donut: 240,
  };
  const explicitHeight = typeof height === "number" ? height : 0;
  const typedMin = chartTypeMin[viz.type] ?? 200;
  const minH = Math.max(explicitHeight, typedMin);
  // shell so the dimensions are always concrete by the time init runs.
  return (
    <div
      ref={containerRef}
      data-floats-chart
      role="img"
      aria-label={ariaSummary}
      style={{
        position: "relative",
        flex: 1,
        minHeight: minH,
        width: "100%",
      }}
    >
      <ReactECharts
        ref={chartRef}
        option={option}
        notMerge
        lazyUpdate
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
        }}
        onEvents={onEvents}
        opts={{ renderer: "canvas" }}
      />
    </div>
  );
}
